<script setup lang="ts">
import {
  type Timeline,
  type BlockData,
  type BlockDataState,
  type BlockDataGeneral,
  type StartedBlockData,
  casesFromBlock,
} from '~/types/blockData';
import { io } from 'socket.io-client';
import type { CaseData, StartedCaseData } from '~/types/caseData';

import { getDoctorImageFromName, getDoctorTitle } from '~/utils/doctorsList';
const router = useRouter();
const route = useRoute();
const config = useRuntimeConfig();
const authStore = useAuthStore();

const isDemoUser = ref(false);
onMounted(async () => {
  isDemoUser.value = await authStore.isDemoUser();
});

const showDialogForConsult = ref(false);
router.beforeEach((to, from) => {
  if (from.path.includes('patients-trackboard') && to.path.includes('patients-consult')) {
    showDialogForConsult.value = true;
    setTimeout(() => {
      showDialogForConsult.value = false;
    }, 4000);
  }
  return true;
});

const api = useAPI();

const confirm = useConfirm();
const toast = useToast();

const blockId = ref('');
const startedBlockId = ref('');

const countdown = ref('00:00');
const timeline = ref<Timeline[]>([]);
const liveTimeline = ref<Timeline[]>([]);
const blockDataState = useState<BlockDataState>('blockDataState', () => ({
  name: '',
  status: '',
  cases: [],
  timeline_started: false,
}));

const showLoadingPopup = ref(false);
const showMoreTimePopup = ref(false);
const hasRespondedToTimeExtension = ref(false);

const formatTime = (secondsLeft: number): string => {
  if (secondsLeft < 0) {
    if (intervalId.value !== null) {
      clearInterval(intervalId.value);
      intervalId.value = null;
    }
    if (blockData.value.block?.showTimer && !hasRespondedToTimeExtension.value) {
      showMoreTimePopup.value = true;
    }
    return '00:00';
  }

  if (
    blockData.value.isBlockCompleted &&
    blockData.value.block?.approximateDuration !== undefined
  ) {
    secondsLeft = blockData.value.block.approximateDuration * 60;
  }

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = Math.floor(secondsLeft % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const addMoreTime = () => {
  showMoreTimePopup.value = false;
  hasRespondedToTimeExtension.value = true;
  let totalTime = blockData.value.block?.approximateDuration || 0;
  if (totalTime > 0) {
    totalTime = totalTime * 60;
  }

  const minutesToAdd = blockData.value.block?.timeExtension || 0;
  startCountdown(totalTime - minutesToAdd * 60, totalTime);
  // do the update-time api call
  if (blockData.value.blockStarted?.id) {
    api(`/play/${blockData.value.blockStarted.id}/update-time`, {
      method: 'PUT',
      body: JSON.stringify({ timeSpent: totalTimePassed.value }),
    });
  }
};

const defaultBlockData = {
  timeline: {
    started: false,
    startFrom: 0,
  },
  isBlockStarted: false,
  isBlockCompleted: false,
  dataLoaded: false,
  blockStarted: undefined,
  liveRoom: false,
  countdown: '00:00',
};

const blockData = ref<BlockDataGeneral>(defaultBlockData);
provide('blockData', blockData);

const socket = ref<any>(null);
const jwtToken = await authStore.getToken();

const setUpLiveRoom = () => {
  if (socket.value) {
    return;
  }

  blockData.value.roomCode = route.query.roomCode as string;
  blockData.value.liveRoom = true;

  const baseUrl = config.public.APIBaseURL;
  const serverUrl = new URL(baseUrl).origin;

  socket.value = io(serverUrl, {
    transports: ['websocket'],
    auth: {
      token: `Bearer ${jwtToken}`,
      Code: blockData.value.roomCode,
    },
  });

  blockData.value.socket = socket.value;
  blockData.value.dataLoaded = true;

  socket.value.on('connect', () => {
    socket.value.emit('listen:player-events', {
      type: 'join-room',
      code: blockData.value.roomCode,
    });
  });

  // socket.value.onAny((event: any, ...args: any) => {
  // });

  const checkIfItsFinished = (data: any) => {
    if (data.finished === true) {
      data.data = data.data || {}; // Ensure data.data is defined
      data.data.completed = true;
    }
    return data;
  };

  socket.value.on('emit:show-distractor', (data: any) => {
    const possibleDistractors = blockData.value.block?.entries?.filter(
      (entry) => entry.type === 'prompt'
    );
    const showDistractor = possibleDistractors?.find(
      (entry) => entry.data.distractor_id === data.distractorId
    );
    if (showDistractor) {
      liveTimeline.value.push({
        ...showDistractor,
        visible: true,
      });
    }
  });

  socket.value.on('emit:case-studies', (data: any) => {
    data = checkIfItsFinished(data);

    blockData.value.blockStarted = blockData.value.blockStarted || { userStartedCaseStudies: [] };
    blockData.value.blockStarted.userStartedCaseStudies =
      blockData.value.blockStarted.userStartedCaseStudies.map((entry) =>
        entry.caseStudyId === data.caseStudyId ? data : entry
      );

    blockData.value.blockStarted?.userStartedCaseStudies?.forEach((startedCase) => {
      const caseEntry = blockData.value.block?.entries?.find(
        (entry) => (entry?.data as CaseData)?.id === startedCase.caseStudyId
      );
      if (caseEntry?.data) {
        startedCase.data = caseEntry.data as CaseData;
      }
    });

    const caseEntry = blockData.value.block?.cases?.find((entry) => entry?.id === data.caseStudyId);

    const alreadyStarted = blockData.value.blockStarted?.userStartedCaseStudies?.some(
      (entry) => entry.caseStudyId === data.caseStudyId
    );

    if (caseEntry && !alreadyStarted) {
      liveTimeline.value.push({
        type: 'case',
        data: {
          ...caseEntry,
        },
        visible: true,
      });
    }

    const startedCasesFinished =
      blockData.value.blockStarted?.userStartedCaseStudies?.every(
        (caseItem) => caseItem.finished === true
      ) || false;

    blockData.value.isBlockCompleted =
      startedCasesFinished &&
      blockData?.value?.blockStarted?.userStartedCaseStudies?.length ===
        blockData.value.block?.cases?.length;

    setTimeout(() => {
      showLoadingPopup.value = false;
    }, 5000);
  });

  socket.value.on('emit:shift-started-event', (data: any) => {
    updateLiveRoomData(data);
  });

  const updateLiveRoomData = (data: any) => {
    if (!data?.userStartedBlock) return;

    blockData.value.block = normalizeBlockData(data.userStartedBlock.block);
    blockData.value.isBlockStarted = true;

    blockData.value.blockStarted = data.userStartedBlock;
    blockData.value.timeline!.started = true;
    blockData.value.block!.cases = casesFromBlock(data.userStartedBlock.block);

    // Set initial countdown value
    let totalTime = data.userStartedBlock.block?.approximateDuration || 0;
    if (totalTime > 0) {
      totalTime = totalTime * 60;
    }
    const formattedTime = formatTime(totalTime);
    countdown.value = formattedTime;
    blockData.value.countdown = formattedTime;

    if (blockData.value.block) {
      blockDataState.value = blockData.value.block;
      blockDataState.value.timeline_started = true;
    }

    if (
      blockData.value.blockStarted?.userStartedCaseStudies?.length != 0 &&
      data.userStartedBlock.block.entries
    ) {
      blockData.value.blockStarted?.userStartedCaseStudies?.forEach((startedCase) => {
        const caseEntry = data.userStartedBlock.block.entries?.find(
          (entry: { data: CaseData }) => (entry?.data as CaseData)?.id === startedCase.caseStudyId
        );
        if (caseEntry?.data) {
          startedCase.data = caseEntry.data as CaseData;
        }
      });
    }
  };
};

onUnmounted(() => {
  if (socket.value) {
    socket.value.off('connect');
    socket.value.off('disconnect');
    socket.value.off('emit:shift-started-event');
    socket.value.off('emit:gm-notify-block');
    socket.value.off('emit:case-studies');
    socket.value.offAny();
    socket.value.disconnect();
    socket.value = null; // Clear the reference
  }
});

const hasRoomCode = route.query.roomCode !== undefined;
if (hasRoomCode) {
  setUpLiveRoom();
}

const normalizeBlockData = (block: BlockData): BlockData => {
  return {
    ...block,
    showTimer: block.showTimer ?? false,
  };
};

const populateBlockData = async (
  redirectTo: any = {},
  hardRefresh: boolean = false,
  runningForDemoUser: boolean = false
) => {
  if (blockId.value === '') {
    return;
  }

  if (hardRefresh) {
    blockData.value = defaultBlockData;
  }

  if (!blockData?.value?.block?.id) {
    const blocksResult: BlockData = await api(`/blocks/${blockId.value}`);
    blockData.value.block = normalizeBlockData(blocksResult);
  }

  let startedBlock: StartedBlockData = await api(`play/started-blocks/block/${blockId.value}`);
  if (isDemoUser.value && !hardRefresh && !runningForDemoUser) {
    startedBlock = {
      userStartedCaseStudies: [],
    } as StartedBlockData;
  }

  if (startedBlock.userStartedCaseStudies.length === 0) {
    blockData.value.isBlockStarted = false;
    blockData.value.isBlockCompleted = false;
  } else {
    blockData.value.isBlockStarted = true;
    blockData.value.blockStarted = startedBlock;

    blockData.value.timeline!.startFrom = startedBlock?.timeSpent || 0;

    // copy over the data from cases from entries, to the userStartedCaseStudies log
    if (
      blockData.value.blockStarted.userStartedCaseStudies.length != 0 &&
      blockData?.value?.block?.entries
    ) {
      blockData.value.blockStarted.userStartedCaseStudies.forEach((startedCase) => {
        const caseEntry = blockData?.value?.block?.entries?.find(
          (entry) => (entry?.data as CaseData)?.id === startedCase.caseStudyId
        );
        if (caseEntry?.data) {
          startedCase.data = caseEntry.data as CaseData;
        }
      });
    }

    blockData.value.isBlockCompleted = blockData.value.blockStarted?.completed;
    if (blockData.value.isBlockCompleted) {
      blockData.value.timeline!.startFrom = 0;
    }
  }

  if (blockData.value.block) {
    blockData.value.block.cases = casesFromBlock(
      blockData.value.block,
      blockData.value.blockStarted?.userStartedCaseStudies
    );
  }
  blockData.value.dataLoaded = true;

  // Set initial countdown value
  let totalTime = blockData?.value?.block?.approximateDuration || 0;
  if (totalTime > 0) {
    totalTime = totalTime * 60;
  }
  const startFrom = blockData.value.timeline!.startFrom || 0;
  const formattedTime = formatTime(totalTime - startFrom);
  if (intervalId.value === null) {
    countdown.value = formattedTime;
    blockData.value.countdown = formattedTime;
  }

  timeline.value =
    blockData?.value?.block?.entries?.map((entry) => {
      return {
        type: entry.type,
        timerValue: entry.timer,
        data: entry.data,
        visible: blockData?.value?.blockStarted?.userStartedCaseStudies?.find(
          (startedCase) => startedCase.caseStudyId === (entry.data as CaseData).id
        )
          ? false
          : true,
      };
    }) || [];

  if (blockDataState.value && blockDataState.value.refresh) {
    blockDataState.value.refresh = false;
  }

  if (redirectTo.queryParams) {
    setTimeout(() => {
      showStartCaseDialog.value = false;
      router.push(
        `/blocks/${redirectTo.blockId}/${redirectTo.startedBlockId}/patients-consult/?${redirectTo.queryParams}`
      );
    }, 1000);
  } else if (redirectTo && Object.keys(redirectTo).length > 0) {
    router.push(redirectTo);
    blockDataState.value.redirectTo = '';
  } else if (typeof redirectTo === 'string' && redirectTo.length > 0) {
    router.push(redirectTo);
    blockDataState.value.redirectTo = '';
  }
};

const updateBlockData = async () => {
  const focusedCaseStudy = blockData.value.blockStarted?.userStartedCaseStudies?.find(
    (caseStudy) => caseStudy.caseStudyId === route.query.focus
  );

  showLoadingPopup.value = true;
  currentOrderMessage.value = getRandomOrderMessage();
  const focusedCaseStudyId = focusedCaseStudy ? focusedCaseStudy.id : null;

  const caseDetails: StartedCaseData = await api(
    `/play/${route.params.startedBlockId}/${focusedCaseStudyId}/details`
  );

  if (blockData.value.blockStarted?.userStartedCaseStudies) {
    const caseIndex = blockData.value.blockStarted?.userStartedCaseStudies.findIndex(
      (caseStudy) => caseStudy.caseStudyId === route.query.focus
    );

    if (caseIndex !== -1) {
      blockData.value.dataLoaded = false;

      // Preserve the existing case structure while updating with new details
      blockData.value.blockStarted.userStartedCaseStudies[caseIndex] = {
        ...blockData.value.blockStarted?.userStartedCaseStudies[caseIndex],
        ...caseDetails,
        currentState: {
          ...blockData.value.blockStarted?.userStartedCaseStudies[caseIndex].currentState,
          ...caseDetails.currentState,
        },
      };
      await nextTick();
      blockData.value.dataLoaded = true;
    }

    const cases = blockData.value.block
      ? casesFromBlock(blockData.value.block, blockData.value.blockStarted?.userStartedCaseStudies)
      : [];
    blockData.value.isBlockCompleted =
      cases.length > 0 && cases.every((caseItem) => caseItem.completed);
  }
  setTimeout(() => {
    showLoadingPopup.value = false;
  }, 3000);
};

watch(
  () => route.query,
  async (newValue) => {
    if (newValue.roomCode) {
      if (!socket.value) {
        setUpLiveRoom();
      }
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => route.params,
  async (newValue) => {
    if (!newValue.blockId) {
      return;
    }

    startedBlockId.value = newValue.startedBlockId as string;

    // Skip if the block ID hasn't changed
    if (newValue.blockId === blockId.value) {
      return;
    }

    blockId.value = newValue.blockId as string;
    if (!blockData.value.liveRoom && !blockData.value.dataLoaded) {
      await populateBlockData();
    }
  },
  { immediate: true, deep: true }
);

const showBackToTrackboard = computed(() => {
  return route.path.includes('/patients-consult');
});

const toTrackboard = async () => {
  const queryParams = new URLSearchParams();
  if (route.query.roomCode) {
    queryParams.append('roomCode', route.query.roomCode as string);
  }
  router.push(
    `/blocks/${blockId.value}/${startedBlockId.value}/patients-trackboard?${queryParams.toString()}`
  );
};

const totalTimePassed = ref(0);

const startCountdown = (startFrom: number, totalTime: number) => {
  // Clear any existing interval first
  if (intervalId.value !== null) {
    clearInterval(intervalId.value);
    intervalId.value = null;
  }

  totalTimePassed.value = startFrom;
  lastUpdateTime.value = 0;

  intervalId.value = window.setInterval(() => {
    totalTime--;
    totalTimePassed.value++;
    lastUpdateTime.value++;

    if (totalTime <= 0) {
      clearInterval(intervalId.value!);
      intervalId.value = null;
      countdown.value = '00:00';
      blockData.value.countdown = '00:00';
      return;
    }
    countdown.value = formatTime(totalTime - startFrom);
    blockData.value.countdown = countdown.value;

    if (lastUpdateTime.value >= 10 && blockData.value.blockStarted?.id) {
      lastUpdateTime.value = 0;
      try {
        if (!isDemoUser.value || !blockData.value.liveRoom) {
          api(`/play/${blockData.value.blockStarted.id}/update-time`, {
            method: 'PUT',
            body: JSON.stringify({ timeSpent: totalTimePassed.value }),
          });
        }
      } catch (error) {
        console.error('Failed to update time spent:', error);
      }
    }

    if (blockData.value.liveRoom) {
      return;
    }

    timeline.value.forEach((timelineEntry) => {
      if (timelineEntry.timerValue === totalTimePassed.value && timelineEntry.visible) {
        liveTimeline.value.push(timelineEntry);
        timelineEntry.visible = true;
      }
    });
  }, 1000);
};

const saveAndExit = async () => {
  confirm.require({
    message: 'Do you want to save and exit?',
    header: 'Exit block',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Exit',
      severity: 'danger',
    },
    accept: async () => {
      if (intervalId.value !== null) {
        clearInterval(intervalId.value);
        intervalId.value = null;
      }

      // Update the database with the latest time spent
      if (blockData.value.blockStarted?.id && !blockData.value.liveRoom) {
        try {
          await api(`/play/${blockData.value.blockStarted.id}/update-time`, {
            method: 'PUT',
            body: JSON.stringify({ timeSpent: totalTimePassed.value }),
          });
        } catch (error) {
          console.error('Failed to update time spent:', error);
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save progress.',
            life: 3000,
          });
        }
      }

      blockDataState.value.timeline_started = false;
      // blockData.value.timeline!.started = false;
      // navigateTo('/modules');
      const moduleId = blockData.value.block?.moduleId;
      window.location.href = moduleId ? `/modules/view/${moduleId}` : '/modules';
    },
    reject: () => {
      toast.add({ severity: 'info', summary: 'Rejected', detail: 'You canceled.', life: 3000 });
    },
  });
};

const restartShift = async () => {
  confirm.require({
    message: 'Are you sure you want to restart this shift? All current progress will be lost.',
    header: 'Restart Shift',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Restart',
      severity: 'danger',
    },
    accept: async () => {
      try {
        if (intervalId.value !== null) {
          clearInterval(intervalId.value);
          intervalId.value = null;
        }

        await api<StartedBlockData>(
          `/play/${blockData.value.block!.id}/start-block`,
          { method: 'POST' },
        );

        blockDataState.value.timeline_started = false;
        const moduleId = blockData.value.block?.moduleId;
        window.location.href = moduleId ? `/modules/view/${moduleId}` : '/modules';
      } catch (error) {
        console.error('Failed to restart shift:', error);
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to restart shift.',
          life: 3000,
        });
      }
    },
    reject: () => {},
  });
};

const lastUpdateTime = ref(0);
const intervalId = ref<number | null>(null);

// Watch for refresh flag
watch(
  () => blockDataState.value?.refresh,
  (newValue) => {
    if (newValue && !blockData.value.liveRoom) {
      updateBlockData();
      blockDataState.value.refresh = false;
    }
  }
);

watch(
  () => blockDataState.value?.showLoading,
  (newValue) => {
    if (!blockData.value.liveRoom && !showLoadingPopup.value) {
      showStartCaseDialog.value = !!newValue;
      setTimeout(() => {
        showStartCaseDialog.value = false;
      }, 5000);
    }
  }
);

// Watch for hard refresh flag
watch(
  () => blockDataState.value?.hardRefresh,
  (newValue) => {
    if (newValue && !blockData.value.liveRoom) {
      const redirectTo = blockDataState.value.redirectTo || '';
      populateBlockData(redirectTo, true);
    }
  }
);

// Watch for patients consult loaded
watch(
  () => blockDataState.value?.patientsConsultLoaded,
  (newValue) => {
    if (newValue) {
      setTimeout(() => {
        showDialogForConsult.value = false;
      }, 1000);
    }
  }
);

// Watch for timeline started
watch(
  () => blockDataState.value?.timeline_started,
  (newValue) => {
    if (newValue) {
      // Only start countdown if it's not already running
      if (intervalId.value === null) {
        const startFrom = blockData.value?.timeline?.startFrom || 0;
        let totalTime = blockData.value?.block?.approximateDuration || 0;

        if (totalTime > 0) {
          totalTime = totalTime * 60;
          startCountdown(startFrom, totalTime);
          blockData.value.timeline!.started = true;
        }
      }
    }
  }
);

const showStartCaseDialog = ref(false);

const randomOrderMessages = [
  'Orders for patient [last name] are under way!',
  'Orders received for patient [last name], implementing now!',
  'Copy that. Executing orders for patient [last name]!',
  'On it. Getting your orders for patient [last name] done now!',
  'In motion on the orders for patient [last name]!',
  'OK, got it! Getting all those orders going for patient [last name].',
  'Yes boss. Working on your orders for patient [last name].',
  'The team is getting rolling on your orders for patient [last name]!',
];

const currentOrderMessage = ref('');

const getRandomOrderMessage = () => {
  const focusedCaseStudy = blockData.value.blockStarted?.userStartedCaseStudies?.find(
    (caseStudy) => caseStudy.caseStudyId === route.query.focus
  );
  const lastName = focusedCaseStudy?.data?.patientNameLast || '';
  const randomMessage = randomOrderMessages[Math.floor(Math.random() * randomOrderMessages.length)];
  return randomMessage.replace('[last name]', lastName);
};

const startCase = async (timelineEntry: Timeline, seePatient: boolean = true) => {
  if (!showLoadingPopup.value) {
    showStartCaseDialog.value = true;

    setTimeout(() => {
      showStartCaseDialog.value = false;
    }, 5000);
  }

  timelineEntry.visible = false;
  if (!socket.value) {
    try {
      await api<CaseData>(`/play/${startedBlockId.value}/${timelineEntry.data.id}/start-case`, {
        method: 'POST',
      });
    } catch (error) {
      console.log(error);
    }
  }

  const queryParams = new URLSearchParams({ focus: timelineEntry.data.id });

  if (route.query.roomCode) {
    queryParams.append('roomCode', route.query.roomCode as string);
  }

  let redirectTo: any = {
    blockId: blockId.value,
    startedBlockId: startedBlockId.value,
    queryParams: queryParams.toString(),
  };

  if (seePatient === false) {
    redirectTo = {};
  }

  if (isDemoUser.value) {
    populateBlockData(redirectTo, false, true);
  } else {
    populateBlockData(redirectTo);
  }
};

const decodeHtmlText = (text: string | undefined) => {
  return removeNbsp(text || '');
};

const selectedOption = ref('');

const hideEntry = (entry: Timeline) => {
  setTimeout(() => {
    entry.visible = false;
    selectedOption.value = '';
  }, 400);
};

const computedOptions = (entry: Timeline) => {
  if (
    entry.data.options &&
    typeof entry.data.options === 'object' &&
    !Array.isArray(entry.data.options)
  ) {
    return Object.entries(entry.data.options)
      .filter(([_key, value]) => value && value !== '')
      .map(([_key, value]) => value);
  }
  return Array.isArray(entry.data.options)
    ? entry.data.options.filter((option: any) => option.value)
    : [];
};

const togglePopup = () => {
  liveTimeline.value.push({
    type: 'case',
    data: {
      introTitle: 'Test Introduction Title',
      popupText:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nam sed eros ut lacus sollicitudin luctus. Praesent in suscipit augue, lacinia feugiat arcu. Duis ac pretium lorem, ac sodales nisl. Sed vitae ipsum sapien. Aliquam a tempor arcu. Etiam mattis dignissim lacinia. In hac habitasse platea dictumst. Vestibulum interdum finibus mass.',
      casePopUp: {
        s3FilePath: 'https://placehold.co/600x400',
      },
    },
    visible: true,
  });
};

const toggleDistractor = () => {
  liveTimeline.value.push({
    type: 'prompt',
    data: {
      signedMedia: 'https://placehold.co/600x400',
      name: 'Jake Weller, RN',
      prompt:
        'This is a mock prompt for testing purposes. It contains sample data to simulate a real-world scenario.',
      options: {
        option1: 'Option 1',
        option2: 'Option 2',
        option3: 'Option 3',
      },
    },
    visible: true,
  });
};

const audioRef = ref<HTMLAudioElement | null>(null);
const isMuted = ref(false);

onMounted(() => {
  // Some browsers block autoplay unless triggered by user interaction
  const playAudio = () => {
    audioRef.value?.play();
    document.removeEventListener('click', playAudio); // Clean up
  };

  // Attempt immediate play
  audioRef.value?.play().catch(() => {
    // Wait for user interaction if autoplay fails
    document.addEventListener('click', playAudio);
  });
});

const toggleMute = () => {
  if (audioRef.value) {
    isMuted.value = !isMuted.value;
    audioRef.value.muted = isMuted.value;
  }
};

</script>

<template>
  <!DOCTYPE html>
  <Html lang="en">
    <Head>
      <Title>Rapid Sequence</Title>
    </Head>
  </Html>

  <Body class="">
    <Toast />
    <ConfirmDialog></ConfirmDialog>

    <div class="hidden">
      <audio ref="audioRef" autoplay loop hidden preload="auto" volume="0.3">
        <source src="/background.mp3" type="audio/mpeg" />
      </audio>
    </div>

    <div
      class="header_bar fixed top-0 left-0 z-50 px-7 w-full py-3.5 bg-white items-center flex justify-between hidden"
    >
      <div class="col_left font-bold text-base">
        {{ blockData?.block?.name }}
        <Tag
          v-if="blockData.liveRoom"
          severity="danger"
          :value="`Live Room, Code: ${blockData.roomCode}`"
          class="ml-2"
        >
        </Tag>
      </div>

      <div class="col_right"></div>
    </div>
    <div
      class="header_under_bar fixed top-0 left-0 z-50 bg-black text-white text-center px-7 py-1.5 w-full flex justify-between items-center"
    >
      <div class="flex-1 text-left flex items-center">
        <template v-if="showBackToTrackboard">
          <p class="mr-3 font-bold text-sm cursor-pointer" @click="toTrackboard">
            <i class="pi pi-arrow-left"></i>
            Back to trackboard
          </p>
        </template>

        <Button
          size="small"
          :icon="isMuted ? 'pi pi-volume-off' : 'pi pi-volume-up'"
          text
          aria-label="Toggle Mute"
          @click="toggleMute"
        />
      </div>

      <div class="absolute left-1/2 transform -translate-x-1/2 text-sm font-bold">
        {{ blockData.block?.title }}
        <template v-if="blockData.block?.showTimer">
          |
          <template v-if="!blockData.isBlockCompleted">Timer {{ countdown }}</template>
          <template v-else>Shift over!</template>
        </template>
      </div>

      <div class="flex-1 text-right">
        <Button
          variant="outlined"
          size="small"
          class="font-bold text-sm !bg-white mr-2"
          severity="danger"
          @click="restartShift"
          >Restart Shift</Button
        >
        <Button
          variant="outlined"
          size="small"
          class="font-bold text-sm !bg-white"
          severity="warn"
          @click="saveAndExit"
          >Save Progress and Exit</Button
        >
        <template v-if="false">
          <Button @click="togglePopup">Toggle Popup</Button>
          <Button @click="toggleDistractor">Toggle Distractor</Button>
        </template>
      </div>
    </div>

    <div v-for="(timelineEntry, index) in liveTimeline" :key="index" class="block_content bg-white">
      <Dialog
        v-if="timelineEntry.type === 'prompt'"
        modal
        :visible="timelineEntry.visible"
        class="!w-[1100px]"
        maximizable
        :closable="true"
        @update:visible="timelineEntry.visible = $event"
      >
        <template #header>
          <div class="text-lg font-semibold"></div>
        </template>
        <div class="p-8">
          <div>
            <div class="flex flex-col items-center gap-8">
              <div class="prompt_row w-full text-center">
                <img
                  v-if="timelineEntry.data.signedMedia"
                  :src="timelineEntry.data.signedMedia"
                  class="w-full object-contain"
                  alt="Prompt Media"
                />
              </div>
              <div class="flex w-full gap-4">
                <div v-if="false" class="prompt_row w-full">
                  <div class="mt-5 flex items-center gap-4 font-digistrip">
                    <div class="mr-2 mb-2 min-w-[115px] max-w-[115px] flex-shrink-0">
                      <img
                        :src="getDoctorImageFromName(timelineEntry.data.name)"
                        class="min-w-[115px] max-w-[115px] w-[115px] h-auto object-contain"
                        style="width: 115px !important"
                      />
                      <div class="prompt_text font-bold text-xs mt-2">
                        {{ timelineEntry.data.name }}
                      </div>
                      <div class="prompt_text font-bold text-xs mt-2">
                        {{ getDoctorTitle(timelineEntry.data.name) }}
                      </div>
                    </div>
                    <div class="prompt_column speech-bubble go_right mb-2">
                      <div class="prompt_text">{{ timelineEntry.data.prompt }}</div>
                    </div>
                  </div>
                </div>
                <div class="flex prompt_row w-full text-left h-full items-center gap-4">
                  <div
                    v-for="(option, index_i) in computedOptions(timelineEntry)"
                    :key="index_i"
                    class="flex items-center radio_wrapper gap-2 w-full"
                  >
                    <RadioButton
                      v-model="selectedOption"
                      :input-id="option"
                      :value="option"
                      @change="hideEntry(timelineEntry)"
                    />
                    <label :for="option">{{ option }}</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        v-if="timelineEntry.type === 'case'"
        modal
        :visible="timelineEntry.visible"
        :show-header="false"
        class="!w-[1100px]"
      >
        <div class="p-8 pb-0 -mb-6">
          <div>
            <div class="flex gap-4 items-end">
              <div class="w-full text-center">
                <img
                  :src="timelineEntry.data.casePopUp?.s3FilePath"
                  class="mx-auto max-w-full h-auto object-contain"
                  alt=""
                />
              </div>
              <div class="w-full ml-6 text-left h-full items-center font-digistrip pb-10">
                <div class="">
                  <div class="popup_intro_title font-bold text-lg mb-5 text-red-700">
                    {{ timelineEntry.data?.introTitle }}
                  </div>
                  <div v-html="decodeHtmlText(timelineEntry.data?.popupText)"></div>
                </div>

                <Button size="small" class="mt-6 mr-10" @click="startCase(timelineEntry)"
                  >Go see the patient</Button
                >
                <Button size="small" class="mt-6" @click="startCase(timelineEntry, false)"
                  >See the patient later</Button
                >
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>

    <Dialog modal :visible="showLoadingPopup" :show-header="false" class="!w-[1100px]">
      <div class="p-2">
        <div class="border-4 border-[#75cb02] rounded-lg -mb-6 -ml-6 -mr-6 p-4 pt-6 pb-6">
          <div class="flex items-center">
            <div class="w-full text-center h-full items-center font-digistrip text-xl">
              <p>{{ currentOrderMessage }}</p>
            </div>
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog v-if="blockData.block?.showTimer" modal :visible="showMoreTimePopup" :show-header="false" class="!w-[1100px]">
      <div class="p-8 pt-14">
        <div>
          <div class="flex items-center">
            <div class="flex flex-col gap-4 w-full text-center h-full items-center">
              <p class="font-digistrip">
                Your shift is over! Do you want to stay for another
                {{ blockData.block?.timeExtension }} minutes after your shift?
              </p>

              <div class="flex gap-4">
                <Button @click="addMoreTime()">Yes</Button>
                <Button severity="danger" @click="showMoreTimePopup = false">No</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>

    <Dialog
      modal
      :visible="showDialogForConsult || showStartCaseDialog"
      :show-header="false"
      class="!w-[1100px]"
    >
      <div class="p-2">
        <div class="border-4 border-black rounded-lg -mb-6 -ml-6 -mr-6 p-4 pt-6 pb-6">
          <div class="flex items-center">
            <div class="w-full text-3xl text-center h-full items-center font-digistrip">
              <!-- <p>Opening patient chart</p> -->

              <p><img src="~/assets/images/load.gif" alt="Loading..." class="mx-auto" /></p>
            </div>
          </div>
        </div>
      </div>
    </Dialog>

    <div class="block_content pt-[97px]">
      <slot v-if="authStore.isAuthenticated" />
      <div v-else>You are not authenticated.</div>
    </div>

  </Body>
</template>

<style>
body .p-splitbutton-button {
  display: none;
}

body .p-splitbutton-dropdown {
  border-radius: 6px;
}

.radio_wrapper {
  border: 1px solid #000;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 6px;
  position: relative;
  cursor: pointer;
}

.radio_wrapper label {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 42px;
}

.radio_wrapper .p-radiobutton {
  position: relative;
  z-index: 1;
}

.radio_wrapper .p-radiobutton-icon,
.radio_wrapper .p-radiobutton-box {
  transition-duration: 0s !important;
  transition-delay: 0s !important;
}

.div-border {
  border-radius: 0.4em;
  border: 4px solid #333;
  border-radius: 30px;
}

.speech-bubble {
  position: relative;
  background: #fff;
  border-radius: 0.4em;
  border: 4px solid #333;
  padding: 20px;
  border-radius: 30px;
  min-height: 150px;
}

.speech-bubble:after {
  content: '';
  position: absolute;
  left: 0;
  top: 10%;
  width: 0;
  height: 0;
  border: 20px solid transparent;
  border-right-color: #333;
  border-left: 0;
  border-top: 0;
  margin-top: 20px;
  margin-left: -20px;
}

.speech-bubble:after {
  display: none;
}

.speech-bubble.go_right:before {
  content: '';
  position: absolute;
  right: 0;
  top: 10%;
  width: 0;
  height: 0;
  border: 20px solid transparent;
  border-left-color: #333;
  border-right: 0;
  border-top: 0;
  margin-top: 20px;
  margin-right: -20px;
}
</style>
